import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import {
  ArrowRight, Mic, Brain, Code2, Sparkles, ShieldCheck, Zap, BarChart3,
  Users, HeadphonesIcon, HandCoins, PlayCircle, ChevronDown, HelpCircle,
  Phone, ClipboardList, FileText, FileSearch, Mail, Camera,
  CheckCircle2, Monitor, Laptop2, Activity, Cpu, Waypoints, Crosshair, Radio,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const LinkedinIcon = ({ className, strokeWidth = 2 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

// ── CSS Animations ────────────────────────────────────────────────────────────
const STYLES = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes floatA {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    40%      { transform: translateY(-28px) translateX(10px); }
    70%      { transform: translateY(10px) translateX(-8px); }
  }
  @keyframes glowPulse {
    0%, 100% { opacity: 0.35; }
    50%       { opacity: 0.70; }
  }
  @keyframes scanlineMove {
    0%   { transform: translateY(-100%); opacity: 0; }
    5%   { opacity: 0.25; }
    95%  { opacity: 0.25; }
    100% { transform: translateY(100vh); opacity: 0; }
  }
  @keyframes shimmerText {
    0%   { background-position: 200% center; }
    100% { background-position: -200% center; }
  }
  @keyframes borderGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(211,255,36,0); }
    50%       { box-shadow: 0 0 24px 2px rgba(211,255,36,0.18); }
  }
  @keyframes ticker {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @keyframes badgeIn {
    from { opacity: 0; transform: translateY(5px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Futuristic / HUD */
  @keyframes auroraDrift {
    0%   { transform: translate3d(-4%, -2%, 0) rotate(0deg) scale(1.05); }
    50%  { transform: translate3d(4%, 3%, 0) rotate(8deg) scale(1.15); }
    100% { transform: translate3d(-4%, -2%, 0) rotate(0deg) scale(1.05); }
  }
  @keyframes spinConic { to { transform: rotate(360deg); } }
  @keyframes hudSpin { to { transform: rotate(360deg); } }
  @keyframes hudSpinRev { to { transform: rotate(-360deg); } }
  @keyframes gridPan {
    from { background-position: 0 0, 0 0; }
    to   { background-position: 60px 60px, 60px 60px; }
  }
  @keyframes waveBar {
    0%, 100% { transform: scaleY(0.22); }
    50%      { transform: scaleY(1); }
  }
  @keyframes caretBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
  @keyframes streamIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes consoleIn {
    from { opacity: 0; transform: perspective(1600px) rotateX(14deg) translateY(48px) scale(0.96); }
    to   { opacity: 1; transform: perspective(1600px) rotateX(4deg) translateY(0) scale(1); }
  }
  @keyframes chipFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-8px); }
  }
  @keyframes arcPulse {
    0%, 100% { opacity: 0.55; filter: drop-shadow(0 0 6px rgba(0,163,255,0.5)); }
    50%       { opacity: 1;    filter: drop-shadow(0 0 16px rgba(0,163,255,0.9)); }
  }
  @keyframes barFill { from { transform: scaleX(0.05); } to { transform: scaleX(1); } }
  @keyframes flicker {
    0%, 100% { opacity: 1; }
    92%      { opacity: 1; }
    93%      { opacity: 0.55; }
    94%      { opacity: 1; }
    97%      { opacity: 0.7; }
    98%      { opacity: 1; }
  }

  /* Scroll-reveal */
  .sr {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.75s cubic-bezier(0.16,1,0.3,1), transform 0.75s cubic-bezier(0.16,1,0.3,1);
  }
  .sr.sr-v { opacity: 1; transform: translateY(0); }
  .sr-d1 { transition-delay: 0.10s; }
  .sr-d2 { transition-delay: 0.20s; }
  .sr-d3 { transition-delay: 0.30s; }
  .sr-d4 { transition-delay: 0.40s; }

  /* Animated gradient headline */
  .shimmer-headline {
    background: linear-gradient(110deg, #ffffff 15%, #D3FF24 35%, #00A3FF 50%, #D3FF24 65%, #ffffff 85%);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmerText 6s linear infinite;
  }

  /* Film grain overlay */
  .grain::after {
    content: '';
    position: fixed;
    inset: -200%;
    z-index: 60;
    pointer-events: none;
    opacity: 0.32;
    mix-blend-mode: overlay;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* Gradient-ring CTA */
  .cta-primary { animation: borderGlow 3.5s ease-in-out infinite; }
  .cta-ring { position: relative; isolation: isolate; }
  .cta-ring::before {
    content: '';
    position: absolute;
    inset: -1.5px;
    z-index: -1;
    border-radius: inherit;
    background: conic-gradient(from 0deg, transparent 0deg, #D3FF24 60deg, #00A3FF 140deg, transparent 220deg, transparent 360deg);
    animation: spinConic 4s linear infinite;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .cta-ring:hover::before { opacity: 1; }

  /* Cursor spotlight cards */
  .spotlight { position: relative; }
  .spotlight::after {
    content: '';
    position: absolute; inset: 0;
    pointer-events: none; opacity: 0;
    background: radial-gradient(340px circle at var(--mx, 50%) var(--my, 50%), rgba(var(--glow, 0,163,255), 0.12), transparent 60%);
    transition: opacity 0.35s ease;
  }
  .spotlight:hover::after { opacity: 1; }

  /* Ticker */
  .ticker-track { animation: ticker 228s linear infinite; white-space: nowrap; }
  .ticker-track:hover { animation-play-state: paused; }

  /* Feature card top-line reveal */
  .feat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(211,255,36,0.55), transparent);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  .feat-card:hover::before { transform: scaleX(1); }

  /* Step number glow */
  .step-num { text-shadow: 0 0 20px rgba(211,255,36,0.5); }

  /* HUD utilities */
  .hud-spin { animation: hudSpin 26s linear infinite; transform-origin: center; }
  .hud-spin-rev { animation: hudSpinRev 34s linear infinite; transform-origin: center; }
  .arc-pulse { animation: arcPulse 3.6s ease-in-out infinite; }
  .telemetry-bar { transform-origin: left; animation: barFill 1.4s cubic-bezier(0.16,1,0.3,1) both; }
  .flicker { animation: flicker 7s linear infinite; }

  /* Live console */
  .console-in { animation: consoleIn 1s cubic-bezier(0.16,1,0.3,1) 0.35s both; }
  .wave-bar { transform-origin: bottom; animation: waveBar 1.1s ease-in-out infinite; }
  .caret { animation: caretBlink 1s step-end infinite; }
  .stream-line { animation: streamIn 0.5s cubic-bezier(0.16,1,0.3,1) both; }
  .float-chip { animation: chipFloat 5s ease-in-out infinite; }

  /* Brand wing flap */
  .brand-wing { will-change: transform; backface-visibility: hidden; }
  .brand-wing-l { transform-origin: 10.3% 42%; animation: flapWingL 1.4s ease-in-out infinite; }
  .brand-wing-r { transform-origin: 23.3% 42%; animation: flapWingR 1.4s ease-in-out infinite; }
  @keyframes flapWingL { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(11deg); } }
  @keyframes flapWingR { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(-11deg); } }

  @media (prefers-reduced-motion: reduce) {
    .brand-wing-l, .brand-wing-r, .wave-bar, .float-chip, .cta-ring::before,
    .console-in, .aurora-blob, .hud-spin, .hud-spin-rev, .arc-pulse,
    .telemetry-bar, .flicker { animation: none !important; }
  }
`;

// ── FEATURE_GROUPS ────────────────────────────────────────────────────────────
const FEATURE_GROUPS = [
  {
    label: "AI Assistant",
    items: [
      { icon: Mic,           label: "Interview Copilot",  desc: "Real-time answers to spoken interview questions",           to: "/app" },
      { icon: Code2,         label: "Coding Interview",   desc: "Analyze on-screen coding problems and generate solutions",  to: "/app?mode=coding" },
      { icon: ClipboardList, label: "Online Assessment",  desc: "Instant help for multiple-choice and image-based questions",to: "/app" },
      { icon: Phone,         label: "Phone Interview",    desc: "Real-time AI assistance on your phone",                    to: "/app/phone" },
      { icon: Brain,         label: "Mock Interview",     desc: "Practice real interview scenarios with an AI interviewer",  to: "/app?mode=mock" },
    ],
  },
  {
    label: "Premium Tools",
    items: [
      { icon: FileText,   label: "Resume Builder",                 desc: "Create ATS-optimized resumes with AI assistance",         to: "/app/resume" },
      { icon: FileSearch, label: "Resume Checker",                 desc: "Analyze and improve your existing resume",                to: "/app/resume/import" },
      { icon: LinkedinIcon,   label: "LinkedIn Profile Optimizer",     desc: "Optimize your LinkedIn profile for better visibility",    to: "/app/linkedin" },
      { icon: Mail,       label: "Cover Letter Generator",         desc: "Generate personalized cover letters instantly",           to: "/app/coverletter" },
      { icon: Camera,     label: "Professional Headshot Generator",desc: "Transform photos into professional headshots",            to: "/app/headshot" },
    ],
  },
];

// ── Dropdowns ─────────────────────────────────────────────────────────────────
function FeaturesDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 hover:text-white transition-colors">
        Features <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-[580px] z-50 pt-3">
          <div className="bg-[#0C0C0C]/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/80">
            <div className="grid grid-cols-2 divide-x divide-white/10">
              {FEATURE_GROUPS.map((group) => (
                <div key={group.label} className="p-1">
                  <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-widest font-bold text-[#00A3FF]">{group.label}</div>
                  {group.items.map((item) => {
                    const cls = "flex items-start gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors group";
                    const inner = (
                      <>
                        <item.icon className="w-4 h-4 text-[#00A3FF] mt-0.5 shrink-0" strokeWidth={1.75} />
                        <div>
                          <div className="text-sm font-medium text-white group-hover:text-[#00A3FF] transition-colors">{item.label}</div>
                          <div className="text-xs text-neutral-500 mt-0.5">{item.desc}</div>
                        </div>
                      </>
                    );
                    return item.to
                      ? <Link key={item.label} to={item.to} className={cls}>{inner}</Link>
                      : <a key={item.label} href="#features" className={cls}>{inner}</a>;
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RESOURCE_LINKS = [
  { icon: Users,          label: "Community",   desc: "Connect with fellow candidates & get support", href: "https://discord.gg/t9CrSPRYV" },
  { icon: HeadphonesIcon, label: "Support",     desc: "Get help from our team",               to: "/app/resources" },
  { icon: HandCoins,      label: "Earn with us",desc: "Refer & earn rewards",                  to: "/app/resources" },
  { icon: PlayCircle,     label: "Demo",        desc: "See Wingman in action",                to: "/app/resources" },
  { icon: HelpCircle,     label: "FAQ",         desc: "Common questions answered",            to: "/app/faq" },
];

function ResourcesDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 hover:text-white transition-colors">
        Resources <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-64 z-50 pt-3">
          <div className="bg-[#0C0C0C]/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/80">
            <div className="p-1">
              {RESOURCE_LINKS.map((item) => {
                const cls = "flex items-start gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors group";
                const inner = (
                  <>
                    <item.icon className="w-4 h-4 text-[#00A3FF] mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div>
                      <div className="text-sm font-medium text-white group-hover:text-[#00A3FF] transition-colors">{item.label}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{item.desc}</div>
                    </div>
                  </>
                );
                return item.href
                  ? <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
                  : <Link key={item.label} to={item.to} className={cls}>{inner}</Link>;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".sr"));
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("sr-v");
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.07, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── HUD building blocks ───────────────────────────────────────────────────────
function HudCorners({ color = "#00A3FF", size = 14, inset = -1, opacity = 0.8 }) {
  const base = { position: "absolute", width: size, height: size, pointerEvents: "none", opacity };
  return (
    <>
      <span style={{ ...base, top: inset, left: inset, borderTop: `1.5px solid ${color}`, borderLeft: `1.5px solid ${color}` }} />
      <span style={{ ...base, top: inset, right: inset, borderTop: `1.5px solid ${color}`, borderRight: `1.5px solid ${color}` }} />
      <span style={{ ...base, bottom: inset, left: inset, borderBottom: `1.5px solid ${color}`, borderLeft: `1.5px solid ${color}` }} />
      <span style={{ ...base, bottom: inset, right: inset, borderBottom: `1.5px solid ${color}`, borderRight: `1.5px solid ${color}` }} />
    </>
  );
}

// Concentric "arc reactor" HUD emblem
function ArcReactor({ size = 128, className = "" }) {
  const ticks = Array.from({ length: 48 });
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" className={className} fill="none">
      {/* outer ticks */}
      <g className="hud-spin">
        {ticks.map((_, i) => {
          const a = (i / ticks.length) * Math.PI * 2;
          const r1 = i % 4 === 0 ? 78 : 84;
          const r2 = 92;
          return (
            <line key={i}
              x1={100 + Math.cos(a) * r1} y1={100 + Math.sin(a) * r1}
              x2={100 + Math.cos(a) * r2} y2={100 + Math.sin(a) * r2}
              stroke={i % 4 === 0 ? "#00A3FF" : "rgba(0,163,255,0.35)"} strokeWidth={i % 4 === 0 ? 1.4 : 0.8} />
          );
        })}
      </g>
      {/* dashed ring counter-rotating */}
      <circle cx="100" cy="100" r="68" stroke="rgba(0,163,255,0.55)" strokeWidth="1.2" strokeDasharray="3 9" className="hud-spin-rev" style={{ transformOrigin: "center" }} />
      {/* segmented arcs */}
      <g className="hud-spin" style={{ animationDuration: "18s" }}>
        <path d="M100 44 A56 56 0 0 1 156 100" stroke="#D3FF24" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
        <path d="M100 156 A56 56 0 0 1 44 100" stroke="#00A3FF" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      </g>
      <circle cx="100" cy="100" r="40" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      {/* core */}
      <circle cx="100" cy="100" r="22" fill="rgba(0,163,255,0.10)" className="arc-pulse" />
      <circle cx="100" cy="100" r="22" stroke="#00A3FF" strokeWidth="1.5" className="arc-pulse" />
      <circle cx="100" cy="100" r="8" fill="#D3FF24" className="arc-pulse" />
    </svg>
  );
}

// Large faint targeting reticle for hero background
function HeroReticle() {
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" aria-hidden="true">
      <g className="hud-spin" style={{ transformOrigin: "center" }}>
        <circle cx="300" cy="300" r="250" stroke="rgba(0,163,255,0.10)" strokeWidth="1" strokeDasharray="2 14" />
        <circle cx="300" cy="300" r="210" stroke="rgba(0,163,255,0.08)" strokeWidth="1" />
        {Array.from({ length: 72 }).map((_, i) => {
          const a = (i / 72) * Math.PI * 2;
          const r1 = i % 6 === 0 ? 224 : 234;
          return (
            <line key={i}
              x1={300 + Math.cos(a) * r1} y1={300 + Math.sin(a) * r1}
              x2={300 + Math.cos(a) * 240} y2={300 + Math.sin(a) * 240}
              stroke="rgba(0,163,255,0.18)" strokeWidth="1" />
          );
        })}
      </g>
      <g className="hud-spin-rev" style={{ transformOrigin: "center" }}>
        <circle cx="300" cy="300" r="160" stroke="rgba(211,255,36,0.08)" strokeWidth="1" strokeDasharray="40 20" />
      </g>
    </svg>
  );
}

// ── SpotlightCard ─────────────────────────────────────────────────────────────
function SpotlightCard({ glow = "0,163,255", className = "", children, ...rest }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} style={{ "--glow": glow }} className={`spotlight ${className}`} {...rest}>
      {children}
    </div>
  );
}

// ── LiveConsole (JARVIS-style HUD product mockup) ─────────────────────────────
function LiveConsole() {
  const answerLines = [
    "Open with the STAR frame — set context in one line.",
    "Anchor the impact: “cut p95 latency 38% by sharding the write path.”",
    "Close with the trade-off you'd revisit at 10× scale.",
  ];
  const telemetry = [
    { label: "NEURAL CORE", val: 0.94, color: "#00A3FF" },
    { label: "CONFIDENCE",  val: 0.97, color: "#D3FF24" },
    { label: "CONTEXT MEM", val: 0.82, color: "#00A3FF" },
  ];
  return (
    <div className="console-in relative mx-auto w-full max-w-5xl" style={{ perspective: "1600px" }}>
      {/* Ambient glow behind console */}
      <div className="absolute -inset-8 -z-10 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 60% at 50% 30%, rgba(0,85,255,0.20), transparent 70%)", filter: "blur(30px)" }} />

      {/* Floating latency chip */}
      <div className="float-chip absolute -top-5 right-6 z-20 hidden sm:flex items-center gap-2 bg-[#0A0A0A]/90 backdrop-blur-md border border-[#D3FF24]/30 px-3 py-1.5 shadow-xl shadow-black/50">
        <Zap className="w-3.5 h-3.5 text-[#D3FF24]" strokeWidth={2} />
        <span className="text-[10px] font-mono tracking-wider text-[#D3FF24]">642ms · ANSWER LOCKED</span>
        <HudCorners color="#D3FF24" size={8} inset={-3} />
      </div>

      <div className="relative overflow-hidden rounded-xl border border-[#00A3FF]/20 bg-[#060809]/92 backdrop-blur-xl shadow-[0_40px_120px_-20px_rgba(0,20,60,0.9)]">
        <HudCorners color="#00A3FF" size={18} inset={6} />
        {/* Top hairline sheen */}
        <div className="absolute inset-x-0 top-0 h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(0,163,255,0.7), rgba(211,255,36,0.6), transparent)" }} />

        {/* Window chrome */}
        <div className="flex items-center gap-2 h-11 px-4 border-b border-[#00A3FF]/12 bg-[#00A3FF]/[0.03]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#D3FF24]/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#00A3FF]/70" />
          <div className="ml-3 flex items-center gap-2 text-[11px] font-mono text-[#00A3FF]/80">
            <Waypoints className="w-3.5 h-3.5" strokeWidth={1.75} />
            WINGMAN·OS // live-session
          </div>
          <div className="ml-auto flex items-center gap-2 border border-[#D3FF24]/25 bg-[#D3FF24]/[0.06] px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] pulse-live" />
            <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#D3FF24]">Live · 12:04</span>
          </div>
        </div>

        {/* Body */}
        <div className="grid md:grid-cols-[1fr_1fr_0.72fr] divide-y md:divide-y-0 md:divide-x divide-[#00A3FF]/10">
          {/* Left — listening + transcript */}
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#00A3FF]">
              <Radio className="w-3.5 h-3.5" strokeWidth={2} /> Listening
            </div>

            {/* Waveform */}
            <div className="flex items-end gap-[3px] h-16">
              {Array.from({ length: 40 }).map((_, i) => (
                <span
                  key={i}
                  className="wave-bar flex-1 rounded-sm"
                  style={{
                    background: i % 3 === 0 ? "#D3FF24" : "rgba(0,163,255,0.7)",
                    animationDelay: `${(i % 11) * 0.09}s`,
                    animationDuration: `${0.9 + (i % 5) * 0.12}s`,
                    opacity: 0.55 + (i % 4) * 0.12,
                  }}
                />
              ))}
            </div>

            {/* Interviewer bubble */}
            <div className="stream-line" style={{ animationDelay: "0.5s" }}>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-600 mb-2">Interviewer</div>
              <div className="relative border border-white/[0.08] bg-white/[0.02] p-4 text-sm text-neutral-300 leading-relaxed">
                <HudCorners color="rgba(0,163,255,0.5)" size={9} inset={-1} />
                “Tell me about a time you scaled a system under a tight deadline.”
                <span className="caret inline-block w-[7px] h-[15px] ml-0.5 -mb-0.5 bg-[#00A3FF]" />
              </div>
            </div>
          </div>

          {/* Middle — AI answer */}
          <div className="p-6 space-y-4 bg-[#04060a]/60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#D3FF24]">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Wingman
              </div>
              <div className="flex items-center gap-1.5 text-[9px] font-mono text-neutral-500 border border-white/[0.08] px-2 py-1">
                <Cpu className="w-3 h-3 text-[#00A3FF]" strokeWidth={2} /> GPT-5.2
              </div>
            </div>

            <div className="space-y-2.5">
              {answerLines.map((line, i) => (
                <div
                  key={i}
                  className="stream-line flex items-start gap-2.5 text-sm text-neutral-300 leading-relaxed"
                  style={{ animationDelay: `${0.9 + i * 0.45}s` }}
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D3FF24] shrink-0" />
                  {line}
                </div>
              ))}
              <div className="stream-line flex items-center gap-2 pt-1 text-[11px] font-mono text-neutral-600" style={{ animationDelay: "2.4s" }}>
                generating follow-up prep
                <span className="caret inline-block w-[7px] h-[13px] bg-[#D3FF24]" />
              </div>
            </div>

            <div className="pt-2 flex flex-wrap gap-2">
              {["Behavioral", "STAR", "System scale"].map((t) => (
                <span key={t} className="text-[10px] font-mono tracking-wide text-neutral-400 border border-white/[0.08] bg-white/[0.02] px-2.5 py-1">{t}</span>
              ))}
            </div>
          </div>

          {/* Right — HUD telemetry / arc reactor */}
          <div className="relative p-6 flex flex-col items-center justify-between bg-[#03050a]/70 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-60"
              style={{ background: "radial-gradient(circle at 50% 30%, rgba(0,163,255,0.10), transparent 60%)" }} />
            <div className="relative arc-pulse">
              <ArcReactor size={124} />
            </div>
            <div className="relative w-full space-y-3 mt-4">
              {telemetry.map((t, i) => (
                <div key={t.label} className="space-y-1">
                  <div className="flex justify-between text-[9px] font-mono tracking-widest text-neutral-500">
                    <span>{t.label}</span>
                    <span style={{ color: t.color }}>{Math.round(t.val * 100)}%</span>
                  </div>
                  <div className="h-1 bg-white/[0.06] overflow-hidden">
                    <div className="telemetry-bar h-full" style={{ width: `${t.val * 100}%`, background: t.color, animationDelay: `${0.8 + i * 0.2}s` }} />
                  </div>
                </div>
              ))}
              <div className="flicker flex items-center gap-1.5 pt-1 text-[9px] font-mono tracking-widest text-[#00A3FF]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00A3FF] pulse-live" /> ALL SYSTEMS NOMINAL
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── StatCounter ───────────────────────────────────────────────────────────────
function StatCounter({ value, suffix = "", prefix = "", label }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    let raf, start;
    const dur = 2200;
    const run = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(ease * value));
      if (p < 1) raf = requestAnimationFrame(run);
      else setCount(value);
    };
    raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [active, value]);

  return (
    <div ref={ref} className="group relative flex flex-col items-center py-10 px-6 transition-colors hover:bg-[#00A3FF]/[0.03]">
      <div className="text-4xl lg:text-5xl font-black font-mono tracking-tight">
        <span className="text-neutral-500 text-2xl lg:text-3xl">{prefix}</span>
        <span className="text-white">{count >= 1000 ? (count / 1000).toFixed(count % 1000 === 0 ? 0 : 1) + "K" : count}</span>
        <span className="text-[#D3FF24]">{suffix}</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-2 text-center">{label}</div>
    </div>
  );
}

// ── Ticker ────────────────────────────────────────────────────────────────────
const TICKER_BASE = [
  "🎉  Aryan K. just received an offer from Google",
  "✅  Sarah M. cracked the Stripe SWE interview",
  "🚀  James L. landed at OpenAI",
  "🎯  Priya S. got into Meta's PM program",
  "💼  Lucas T. accepted an offer from McKinsey",
  "✨  Neha R. passed the Amazon SDE loop",
  "🏆  David C. landed a Senior Engineer role at Atlassian",
  "⚡  Rohit P. aced the Google system design round",
  "🎊  Chen W. received an offer from Jane Street",
  "🔥  Maya K. passed the Coinbase technical screen",
  "🎯  Anika S. landed a Product Manager role at Apple",
  "🚀  Ethan B. received an offer from Microsoft",
  "💼  Sofia R. joined Goldman Sachs as an Analyst",
  "🎉  Vikram N. cracked the Netflix SDE-III interview",
  "✅  Olivia T. landed a Data Scientist role at Tesla",
  "🏆  Mohammed A. accepted an offer from SpaceX",
  "⚡  Zara H. joined Boston Consulting Group",
  "🎊  Kevin L. got into Nvidia as a Senior Engineer",
  "🔥  Ishaan M. passed the Palantir technical loop",
  "🎯  Emma W. landed at Salesforce as a Solutions Architect",
  "🚀  Carlos D. received an offer from JPMorgan Chase",
  "💼  Ananya P. joined Bain & Company as a Consultant",
  "🎉  Tyler J. cracked the Uber Engineering interview",
  "✅  Mei L. landed a Machine Learning role at DeepMind",
  "🏆  Ravi K. accepted an offer from Airbnb",
  "⚡  Fatima Z. joined Morgan Stanley as an Associate",
  "🎊  Noah C. received an offer from Spotify",
  "🔥  Divya S. passed the LinkedIn SWE interview",
  "🎯  Liam O. landed a Senior PM role at Adobe",
  "🚀  Shreya G. joined BlackRock as a Quantitative Analyst",
  "💼  Jack M. received an offer from Anthropic",
  "🎉  Pooja R. cracked the Twitter / X engineering screen",
  "✅  Daniel K. landed at Stripe as a Backend Engineer",
  "🏆  Yuna C. accepted an offer from Samsung",
  "⚡  Aarav B. joined Deloitte as a Technology Consultant",
  "🎊  Chloe F. received an offer from Shopify",
  "🔥  Arjun V. passed the Citadel quantitative interview",
  "🎯  Isabella M. landed a DevOps role at Oracle",
  "🚀  Samuel T. joined Two Sigma as a Software Engineer",
  "💼  Nadia H. received an offer from Accenture",
  "🎉  Oscar P. cracked the Lyft infrastructure interview",
  "✅  Tanvi J. landed a Growth PM role at Notion",
  "🏆  Benjamin L. accepted an offer from Palo Alto Networks",
  "⚡  Kavya S. joined Deutsche Bank as a Quantitative Developer",
  "🎊  Ryan C. received an offer from Figma",
  "🔥  Meera N. passed the Dropbox SWE-II loop",
  "🎯  Alex W. landed a Cloud Architect role at AWS",
  "🚀  Simran K. joined McKinsey Digital as an Associate",
  "💼  Ethan R. received an offer from Databricks",
  "🎉  Priyanka M. cracked the Robinhood product interview",
  "✅  Lucas F. landed a Research Scientist role at Microsoft Research",
  "🏆  Aisha O. accepted an offer from Sequoia-backed startup",
  "⚡  Rahul D. joined Qualcomm as a Senior Engineer",
  "🎊  Hannah T. received an offer from Zoom as a PM",
  "🔥  Siddharth C. passed the Snowflake data engineering screen",
  "🎯  Grace L. landed at HubSpot as a Full-Stack Engineer",
  "🚀  Nia B. joined Goldman Sachs Technology as an Analyst",
  "💼  Aryan M. received an offer from Scale AI",
  "🎉  Sophia K. cracked the Rivian hardware interview",
  "✅  Akshay P. landed a Staff Engineer role at Cloudflare",
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  useScrollReveal();

  return (
    <>
      <SEO
        title="AI Interview Coach — Practice & Land Your Dream Job"
        description="Project Wingman is an AI-powered interview coach. Practice live and mock interviews, get real-time feedback, build your resume, and land offers at Google, Amazon, McKinsey and more."
        path="/"
      />
      <style>{STYLES}</style>

      <div className="grain min-h-screen bg-[#030405] text-white overflow-x-hidden">

        {/* ── Header ── */}
        <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#030405]/80 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/wingman-logo.png" alt="Wingman.AI" className="h-10 w-auto" />
              <span className="font-display font-bold tracking-tight text-lg">WINGMAN<span className="text-[#00A3FF]">.</span>AI</span>
            </div>
            <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
              <FeaturesDropdown />
              <a href="#how" className="hover:text-white transition-colors">How it works</a>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <a href="#coding" className="hover:text-white transition-colors">Coding Assistant</a>
              <ResourcesDropdown />
            </nav>
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/app" className="text-sm bg-white text-black px-4 py-2 font-medium hover:bg-neutral-200 transition-colors">Dashboard</Link>
                  <button onClick={() => { logout(); nav("/"); }} className="text-sm text-neutral-400 hover:text-white transition-colors">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="header-login" className="text-sm text-neutral-400 hover:text-white transition-colors">Sign in</Link>
                  <Link to="/register" data-testid="header-register" className="cta-ring text-sm bg-white text-black px-4 py-2 font-medium hover:bg-neutral-200 transition-colors">Get started</Link>
                </>
              )}
            </div>
          </div>
        </header>

        {/* ── Hero ── */}
        <section className="relative overflow-hidden pt-6 pb-16">

          {/* Perspective grid */}
          <div className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(rgba(0,163,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,163,255,0.05) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
              animation: "gridPan 24s linear infinite",
              maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 75%)",
              WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 75%)",
            }} />

          {/* Targeting reticle behind brand */}
          <div className="absolute left-1/2 -translate-x-1/2 top-2 w-[680px] h-[680px] max-w-[95vw] max-h-[95vw] pointer-events-none opacity-70">
            <HeroReticle />
          </div>

          {/* Aurora mesh */}
          <div className="aurora-blob absolute pointer-events-none"
            style={{
              top: "-14%", left: "-6%", width: "620px", height: "620px",
              background: "radial-gradient(circle, rgba(0,85,255,0.22) 0%, transparent 62%)",
              filter: "blur(60px)", animation: "auroraDrift 18s ease-in-out infinite",
            }} />
          <div className="aurora-blob absolute pointer-events-none"
            style={{
              top: "2%", right: "-10%", width: "560px", height: "560px",
              background: "radial-gradient(circle, rgba(0,163,255,0.14) 0%, transparent 62%)",
              filter: "blur(70px)", animation: "auroraDrift 22s ease-in-out infinite 2s reverse",
            }} />

          {/* Scanline sweep */}
          <div className="absolute left-0 right-0 h-px pointer-events-none"
            style={{
              background: "linear-gradient(to right, transparent, rgba(0,163,255,0.4), transparent)",
              animation: "scanlineMove 12s linear infinite 2s",
            }} />

          {/* Bottom fade */}
          <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
            style={{ background: "linear-gradient(to bottom, transparent, #030405)" }} />

          <div className="relative max-w-7xl mx-auto px-6 w-full">
            {/* Brand image */}
            <div className="pt-6" style={{ animation: "fadeInUp 0.6s cubic-bezier(0.16,1,0.3,1) both" }}>
              <div className="relative h-28 sm:h-36 mb-14 mx-auto" style={{ aspectRatio: "5069 / 1116", maxWidth: "640px" }}>
                <img src="/brand-wing-left-v2.png" alt="" aria-hidden="true" className="brand-wing brand-wing-l absolute inset-0 w-full h-full" />
                <img src="/brand-wing-right-v2.png" alt="" aria-hidden="true" className="brand-wing brand-wing-r absolute inset-0 w-full h-full" />
                <img src="/brand-body-v2.png" alt="Wingman.AI — Your AI Interview Co-Pilot" className="absolute inset-0 w-full h-full" />
              </div>
            </div>

            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div style={{ animation: "badgeIn 0.6s cubic-bezier(0.16,1,0.3,1) 0.1s both" }}>
                <div className="relative inline-flex items-center gap-2.5 border border-[#00A3FF]/30 bg-[#00A3FF]/[0.05] px-4 py-2 mb-8">
                  <HudCorners color="#00A3FF" size={7} inset={-2} />
                  <Crosshair className="w-3.5 h-3.5 text-[#00A3FF] hud-spin" strokeWidth={1.75} style={{ animationDuration: "12s" }} />
                  <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#00A3FF]">Real-time AI · combat-ready for live interviews</span>
                </div>
              </div>

              {/* Headline */}
              <div style={{ animation: "fadeInUp 0.75s cubic-bezier(0.16,1,0.3,1) 0.1s both" }}>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] mx-auto">
                  Ace every interview with a wingman that thinks{" "}
                  <span className="shimmer-headline">faster than you can speak.</span>
                </h1>
              </div>

              {/* Sub-headline */}
              <div style={{ animation: "fadeInUp 0.75s cubic-bezier(0.16,1,0.3,1) 0.25s both" }}>
                <p className="mt-7 text-lg text-neutral-400 max-w-xl mx-auto leading-relaxed">
                  Live transcription. Instant answer suggestions. Mock interviews with elite AI models.
                  Built for software engineers, product managers, consultants and beyond.
                </p>
              </div>

              {/* CTAs */}
              <div style={{ animation: "fadeInUp 0.75s cubic-bezier(0.16,1,0.3,1) 0.4s both" }}>
                <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                  <Link
                    to={user ? "/app" : "/register"}
                    data-testid="hero-cta"
                    className="cta-primary cta-ring group inline-flex items-center gap-2 bg-[#D3FF24] text-black px-7 py-4 font-bold uppercase tracking-widest text-xs hover:brightness-105 transition-all"
                  >
                    {user ? "Go to dashboard" : "Start free trial"}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 border border-white/15 px-7 py-4 font-medium text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/30 transition-all"
                  >
                    View pricing
                  </Link>
                  <Link
                    to="/download/windows"
                    className="group inline-flex items-center gap-2.5 border border-white/15 px-7 py-4 font-medium text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/30 transition-all"
                  >
                    <Monitor className="w-4 h-4 text-[#00A3FF] shrink-0" strokeWidth={1.75} />
                    Windows App
                  </Link>
                  <Link
                    to="/download/mac"
                    className="group inline-flex items-center gap-2.5 border border-white/15 px-7 py-4 font-medium text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/30 transition-all"
                  >
                    <Laptop2 className="w-4 h-4 text-[#00A3FF] shrink-0" strokeWidth={1.75} />
                    Mac OS App
                  </Link>
                </div>
              </div>

              {/* Trust badges */}
              <div style={{ animation: "fadeInUp 0.75s cubic-bezier(0.16,1,0.3,1) 0.55s both" }}>
                <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-[11px] uppercase tracking-widest text-neutral-500">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-[#D3FF24]" strokeWidth={1.75} />
                    &lt; 800ms latency
                  </div>
                  <div className="w-px h-3 bg-white/10" />
                  <div className="flex items-center gap-2">
                    <Brain className="w-3.5 h-3.5 text-[#00A3FF]" strokeWidth={1.75} />
                    GPT-5.2 &amp; Claude 4.5
                  </div>
                  <div className="w-px h-3 bg-white/10" />
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.75} />
                    SOC2 ready
                  </div>
                  <div className="w-px h-3 bg-white/10" />
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.75} />
                    Invisible in screen-share
                  </div>
                </div>
              </div>
            </div>

            {/* Product console */}
            <div className="mt-20">
              <LiveConsole />
            </div>
          </div>
        </section>

        {/* ── Ticker strip ── */}
        <div className="relative border-y border-white/[0.05] bg-[#050708] overflow-hidden py-3.5">
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
            style={{ background: "linear-gradient(to right, #050708, transparent)" }} />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
            style={{ background: "linear-gradient(to left, #050708, transparent)" }} />
          <div className="ticker-track inline-flex items-center gap-12">
            {[...TICKER_BASE, ...TICKER_BASE].map((item, i) => (
              <span key={i} className="text-[11px] text-neutral-500 flex-shrink-0 tracking-wide">{item}</span>
            ))}
          </div>
        </div>

        {/* ── Stats ── */}
        <section className="border-b border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-white/[0.06]">
              <StatCounter value={50000} suffix="+" label="Candidates helped" />
              <StatCounter value={800}   prefix="<" suffix="ms" label="Avg response latency" />
              <StatCounter value={97}    suffix="%" label="Satisfaction rate" />
              <StatCounter value={200}   suffix="+" label="Companies cracked" />
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section id="features" className="border-t border-white/[0.06] py-28">
          <div className="max-w-7xl mx-auto px-6">
            <div className="sr mb-3">
              <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-600 font-semibold">/ Features</div>
            </div>
            <h2 className="sr sr-d1 font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl mb-4">
              The unfair advantage in every interview.
            </h2>
            <p className="sr sr-d2 text-neutral-500 max-w-xl mb-16 leading-relaxed">
              Every mode, every format, every stage of the hiring process — covered by AI that thinks as fast as the conversation moves.
            </p>

            {/* 6-card grid */}
            <div className="grid lg:grid-cols-3 gap-px bg-white/[0.06]">
              {[
                { icon: Mic,        t: "Live Mode",      d: "Listens to the interviewer in real-time, transcribes, and surfaces ideal answers as you speak." },
                { icon: Brain,      t: "Mock Mode",      d: "AI plays the interviewer. Tough questions, structured feedback, infinite practice." },
                { icon: Code2,      t: "Coding Mode",    d: "Algorithms, system design, optimal solutions with complexity analysis." },
                { icon: Sparkles,   t: "Model picker",   d: "Switch between Claude Sonnet 4.5 and GPT-5.2 per session." },
                { icon: BarChart3,  t: "Session replay", d: "Every answer captured. Review, learn, improve." },
                { icon: ShieldCheck,t: "Stealth mode",   d: "Invisible in screen-share. Auto-mute on tab switch." },
              ].map((f, i) => (
                <SpotlightCard
                  key={f.t}
                  glow="211,255,36"
                  className={`sr sr-d${(i % 3) + 1} feat-card group bg-[#040608] p-8 hover:bg-[#070b0e] transition-colors duration-300 overflow-hidden`}
                >
                  <div className="relative w-10 h-10 border border-[#D3FF24]/20 bg-[#D3FF24]/[0.04] flex items-center justify-center mb-6 group-hover:border-[#D3FF24]/50 group-hover:bg-[#D3FF24]/[0.08] transition-all duration-300">
                    <HudCorners color="#D3FF24" size={6} inset={-2} opacity={0} />
                    <f.icon className="w-5 h-5 text-[#D3FF24]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2">{f.t}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">{f.d}</p>
                </SpotlightCard>
              ))}
            </div>

            {/* Feature group breakdown */}
            <div className="mt-px grid lg:grid-cols-2 gap-px bg-white/[0.06]">
              {/* AI Assistant */}
              <div className="sr bg-[#040608] p-10">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#D3FF24] font-bold mb-8">AI Assistant</div>
                <div className="space-y-6">
                  {[
                    { icon: Mic,           t: "Interview Copilot",   d: "Real-time answers to spoken interview questions" },
                    { icon: Code2,         t: "Coding Interview",    d: "Analyze on-screen coding problems and generate solutions" },
                    { icon: ClipboardList, t: "Online Assessment",   d: "Instant help for multiple-choice and image-based questions" },
                    { icon: Phone,         t: "Phone Interview",     d: "Real-time AI assistance on your phone" },
                    { icon: Brain,         t: "Mock Interview",      d: "Practice real interview scenarios with an AI interviewer" },
                  ].map((f) => (
                    <div key={f.t} className="flex items-start gap-4 group">
                      <div className="w-9 h-9 border border-white/[0.08] flex items-center justify-center shrink-0 group-hover:border-[#D3FF24]/40 group-hover:bg-[#D3FF24]/[0.04] transition-all duration-200">
                        <f.icon className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">{f.t}</div>
                        <div className="text-xs text-neutral-600 mt-0.5 leading-relaxed">{f.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Premium Tools */}
              <div className="sr sr-d2 bg-[#040608] p-10">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#D3FF24] font-bold mb-8">Premium Tools</div>
                <div className="space-y-6">
                  {[
                    { icon: FileText,   t: "Resume Builder",                 d: "Create ATS-optimized resumes with AI assistance" },
                    { icon: FileSearch, t: "Resume Checker",                 d: "Analyze and improve your existing resume" },
                    { icon: LinkedinIcon,   t: "LinkedIn Profile Optimizer",     d: "Optimize your LinkedIn profile for better visibility" },
                    { icon: Mail,       t: "Cover Letter Generator",         d: "Generate personalized cover letters instantly" },
                    { icon: Camera,     t: "Professional Headshot Generator",d: "Transform photos into professional headshots" },
                  ].map((f) => (
                    <div key={f.t} className="flex items-start gap-4 group">
                      <div className="w-9 h-9 border border-white/[0.08] flex items-center justify-center shrink-0 group-hover:border-[#D3FF24]/40 group-hover:bg-[#D3FF24]/[0.04] transition-all duration-200">
                        <f.icon className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">{f.t}</div>
                        <div className="text-xs text-neutral-600 mt-0.5 leading-relaxed">{f.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section id="how" className="border-t border-white/[0.06] py-28 relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(0,85,255,0.05) 0%, transparent 100%)" }} />

          <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20">
            <div>
              <div className="sr text-[11px] uppercase tracking-[0.35em] text-neutral-600 font-semibold mb-4">/ How it works</div>
              <h2 className="sr sr-d1 font-display text-4xl sm:text-5xl font-bold tracking-tight mb-8">
                Three steps to interview readiness.
              </h2>
              <p className="sr sr-d2 text-neutral-500 leading-relaxed max-w-md">
                Plug in your role, pick a mode, and let Wingman do the heavy lifting. No installs.
                Works in any browser. Use during Zoom, Meet, Teams — the AI is invisible to your interviewer.
              </p>

              {/* Mini feature list */}
              <div className="sr sr-d3 mt-10 space-y-3">
                {[
                  "Works on any platform — Zoom, Meet, Teams, phone calls",
                  "Fully invisible — no overlay, no audio output",
                  "Personalized to your résumé and target role",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-neutral-500">
                    <CheckCircle2 className="w-4 h-4 text-[#00A3FF] mt-0.5 shrink-0" strokeWidth={1.75} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {[
                ["01", "Setup",          "Tell us the company, role, scenario. Upload your résumé for personalization."],
                ["02", "Start session",  "Live transcription kicks in. AI listens, thinks, and surfaces answers."],
                ["03", "Win the offer",  "Practice with mocks. Review every session. Iterate until you're sharp."],
              ].map(([n, t, d], i) => (
                <SpotlightCard key={n} glow="0,163,255"
                  className={`sr sr-d${i + 1} group relative border border-white/[0.07] bg-[#050708] p-6 flex gap-6 hover:border-[#00A3FF]/25 hover:bg-[#070b0e] transition-all duration-300 overflow-hidden`}
                >
                  <HudCorners color="rgba(0,163,255,0.4)" size={10} inset={4} />
                  <div className="step-num font-mono text-3xl font-black text-[#00A3FF] leading-none pt-1 shrink-0">{n}</div>
                  <div>
                    <h3 className="font-display font-semibold text-lg mb-2">{t}</h3>
                    <p className="text-sm text-neutral-500 leading-relaxed">{d}</p>
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </section>

        {/* ── Coding Assistant ── */}
        <section id="coding" className="border-t border-white/[0.06] py-28">
          <div className="max-w-7xl mx-auto px-6">
            <div className="sr text-[11px] uppercase tracking-[0.35em] text-neutral-600 font-semibold mb-4">/ Coding Assistant</div>
            <h2 className="sr sr-d1 font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl mb-6">
              Crack coding rounds with an AI that thinks in algorithms.
            </h2>
            <p className="sr sr-d2 text-neutral-500 max-w-xl mb-14 leading-relaxed">
              From LeetCode-style problems to full system design — Wingman walks you through optimal solutions,
              complexity analysis, and the trade-offs interviewers actually care about.
            </p>

            <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] mb-12">
              {[
                { icon: Code2,  t: "Algorithm Solver",  d: "Paste any coding problem. Get the optimal solution with step-by-step reasoning, time/space complexity, and edge cases covered." },
                { icon: Zap,    t: "System Design",     d: "Tackle scale, architecture, data models and failure modes. Answers calibrated to the company and role you're interviewing for." },
                { icon: Brain,  t: "Language Aware",    d: "Responses use the right language for your role — Python for data science, Go/Java for backend, JavaScript for frontend engineers." },
              ].map((f, i) => (
                <SpotlightCard key={f.t} glow="211,255,36"
                  className={`sr sr-d${i + 1} feat-card group bg-[#040608] p-8 hover:bg-[#070b0e] transition-colors duration-300 overflow-hidden`}
                >
                  <div className="w-10 h-10 border border-[#D3FF24]/20 bg-[#D3FF24]/[0.04] flex items-center justify-center mb-6 group-hover:border-[#D3FF24]/50 transition-all duration-300">
                    <f.icon className="w-5 h-5 text-[#D3FF24]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2">{f.t}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">{f.d}</p>
                </SpotlightCard>
              ))}
            </div>

            <div className="sr sr-d1">
              <Link
                to={user ? "/app" : "/register"}
                className="cta-ring group inline-flex items-center gap-2 bg-[#D3FF24] text-black px-7 py-4 font-bold uppercase tracking-widest text-xs hover:brightness-105 transition-all"
              >
                {user ? "Get started" : "Get started for free"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="border-t border-white/[0.06] py-32 relative overflow-hidden">
          {/* Arc reactor centerpiece */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-30">
            <ArcReactor size={520} />
          </div>
          {/* Ambient radial glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 55% 55% at 50% 50%, rgba(0,163,255,0.06) 0%, rgba(0,85,255,0.04) 40%, transparent 70%)", animation: "glowPulse 6s ease-in-out infinite" }} />
          {/* Corner vignette */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(3,4,5,0.95) 100%)" }} />

          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <div className="sr inline-flex items-center gap-2 border border-[#00A3FF]/25 bg-[#00A3FF]/[0.04] px-4 py-2 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00A3FF] pulse-live" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00A3FF]">50,000+ candidates trust Wingman</span>
            </div>

            <h2 className="sr sr-d1 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-[0.95]">
              Stop preparing.<br />
              Start <span className="shimmer-headline">performing.</span>
            </h2>
            <p className="sr sr-d2 text-neutral-500 mb-12 max-w-lg mx-auto leading-relaxed">
              Join thousands of candidates landing offers at top companies with Project Wingman by their side.
            </p>
            <div className="sr sr-d3 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={user ? "/app" : "/register"}
                data-testid="bottom-cta"
                className="cta-primary cta-ring group inline-flex items-center gap-2 bg-[#D3FF24] text-black px-8 py-4 font-bold uppercase tracking-widest text-xs hover:brightness-105 transition-all"
              >
                {user ? "Get started" : "Get started — free"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-white/15 px-8 py-4 font-medium text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/25 transition-all"
              >
                See pricing <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Final trust strip */}
            <div className="sr sr-d4 mt-12 flex flex-wrap items-center justify-center gap-6 text-[10px] uppercase tracking-widest text-neutral-700">
              <span>No credit card required</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Cancel anytime</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Works in any browser</span>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="border-t border-white/[0.06] py-10">
          <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-600">
            <div className="flex items-center gap-2">
              <img src="/wingman-logo.png" alt="Wingman.AI" className="h-6 w-auto opacity-80" />
              <span className="font-bold tracking-tight text-neutral-400">WINGMAN<span className="text-[#00A3FF]">.</span>AI</span>
              <span className="ml-2">© 2026 Project Wingman · All rights reserved</span>
            </div>
            <div className="flex gap-6">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
